"use client";

import * as Sentry from "@sentry/react";
import type { User } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { Role, Site } from "@/lib/types";

type AuthContextType = {
  user: User | null;
  site: Site | null;
  role: Role | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [site, setSite] = useState<Site | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadUserSite = useCallback(async (userId: string) => {
    try {
      // Get user's site membership
      const { data: siteUsers, error } = await supabase
        .from("site_users")
        .select("site_id, role, sites(id, name)")
        .eq("user_id", userId)
        .limit(1)
        .single();

      if (error) throw error;

      if (siteUsers?.sites) {
        setSite(siteUsers.sites as unknown as Site);
        setRole(siteUsers.role as Role);
      }
    } catch (error) {
      console.error("Error loading site:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserSite(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserSite(session.user.id);
      } else {
        setSite(null);
        setRole(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadUserSite]);

  useEffect(() => {
    try {
      if (user) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
        });

        if (site) {
          Sentry.setTag("site_id", site.id);
          Sentry.setTag("site_name", site.name);
        }

        if (role) {
          Sentry.setTag("role", role);
        }
      } else {
        Sentry.setUser(null);
        Sentry.setTag("site_id", undefined);
        Sentry.setTag("site_name", undefined);
        Sentry.setTag("role", undefined);
      }
    } catch (error) {
      // Non-blocking: silently fail if Sentry integration has issues
      console.warn("Sentry user tracking failed:", error);
    }
  }, [user, site, role]);

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSite(null);
    setRole(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  const contextValue = useMemo(
    () => ({ user, site, role, loading, signOut: handleSignOut }),
    [user, site, role, loading, handleSignOut],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
